/**
 * Wiederverwendbares Interaction-Hint-Overlay für 360°-Viewer
 * (Video360Viewer + Photo360Viewer).
 *
 * Zeigt zentral zwei prominente Icon-Gruppen ("Umschauen" mit
 * 4-Richtungs-Pfeilen auf Perspektiv-Ellipse und "Zoomen" mit Pinch-Icon),
 * darunter dezent Hand + Maus (Touch/Desktop) in Gold.
 *
 * Bleibt permanent sichtbar solange die Overlay-Instanz gemounted ist —
 * kein Auto-Dismiss, kein Interaktions-Dismiss. `pointer-events-none`
 * stellt sicher, dass Drag/Zoom-Events durch das Overlay zum Canvas
 * durchgereicht werden.
 *
 * Wird extrahiert, weil Style/Layout in ~8 Iterationen finetuned wurde
 * und wir Divergenz zwischen Video- und Photo-Viewer vermeiden wollen.
 */
type Props = {
    testIdPrefix?: string;
};

export default function InteractionHintOverlay({ testIdPrefix = "viewer" }: Props) {
    return (
        <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]"
            style={{
                // Permanent sichtbar als dezente Bedienungs-Hilfe. Kein
                // Backdrop, kein Blur, kein dunkler Layer — Icons legen sich
                // transparent über die 360°-Fläche.
                background: "transparent",
                opacity: 0.75,
            }}
            data-testid={`${testIdPrefix}-hint-overlay`}
            aria-hidden="true"
        >
            <div className="flex flex-col items-center gap-4 sm:gap-6 px-6">
                <div className="flex flex-row items-center justify-center gap-10 sm:gap-16">
                    {/* Umschauen — 4-Richtungs-Pfeile auf Perspektiv-Ellipse */}
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-hint-pulse">
                            <svg
                                className="w-14 h-14 sm:w-20 sm:h-20"
                                style={{ color: "hsl(var(--hf-bg))" }}
                                viewBox="0 0 64 64"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                            >
                                <ellipse
                                    cx="32"
                                    cy="32"
                                    rx="26"
                                    ry="16"
                                    style={{ opacity: 0.7 }}
                                />
                                <path
                                    d="M32 16 C 40 22, 40 42, 32 48"
                                    style={{ opacity: 0.3 }}
                                />
                                <path
                                    d="M32 16 C 24 22, 24 42, 32 48"
                                    style={{ opacity: 0.3 }}
                                />
                                <g
                                    style={{ color: "hsl(var(--hf-gold))" }}
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M44 32 L56 32" />
                                    <path d="M52 28 L56 32 L52 36" />
                                    <path d="M20 32 L8 32" />
                                    <path d="M12 28 L8 32 L12 36" />
                                    <path d="M32 20 L32 8" />
                                    <path d="M28 12 L32 8 L36 12" />
                                    <path d="M32 44 L32 56" />
                                    <path d="M28 52 L32 56 L36 52" />
                                </g>
                            </svg>
                        </div>
                        <span
                            className="text-sm sm:text-base font-medium tracking-wide"
                            style={{
                                color: "hsl(var(--hf-bg))",
                                textShadow: "0 1px 4px hsl(var(--hf-ink) / 0.85)",
                            }}
                        >
                            Umschauen
                        </span>
                    </div>

                    {/* Zoomen — Zwei-Finger-Pinch */}
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-hint-pulse">
                            <svg
                                className="w-14 h-14 sm:w-20 sm:h-20"
                                style={{ color: "hsl(var(--hf-bg))" }}
                                viewBox="0 0 64 64"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                            >
                                <circle cx="20" cy="20" r="5" />
                                <circle cx="44" cy="44" r="5" />
                                <path
                                    d="M25 25 L39 39"
                                    style={{ opacity: 0.35, strokeDasharray: "2 4" }}
                                />
                                <g
                                    style={{ color: "hsl(var(--hf-gold))" }}
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M14 14 L8 8" />
                                    <path d="M8 13 L8 8 L13 8" />
                                    <path d="M50 50 L56 56" />
                                    <path d="M56 51 L56 56 L51 56" />
                                </g>
                            </svg>
                        </div>
                        <span
                            className="text-sm sm:text-base font-medium tracking-wide"
                            style={{
                                color: "hsl(var(--hf-bg))",
                                textShadow: "0 1px 4px hsl(var(--hf-ink) / 0.85)",
                            }}
                        >
                            Zoomen
                        </span>
                    </div>
                </div>

                {/* Geräte-Zeile: Hand (Touch) · Maus (Desktop) */}
                <div
                    className="mt-2 sm:mt-4 flex flex-row items-center justify-center gap-4 sm:gap-6"
                    style={{ opacity: 0.75 }}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-6 h-6 sm:w-8 sm:h-8"
                        style={{ color: "#D4AF37" }}
                        aria-hidden
                    >
                        <path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v0" />
                        <path d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2" />
                        <path d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8" />
                        <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 16" />
                    </svg>
                    <span
                        className="text-xs"
                        style={{ color: "rgba(212, 175, 55, 0.65)" }}
                    >
                        ·
                    </span>
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-6 h-6 sm:w-8 sm:h-8"
                        style={{ color: "#D4AF37" }}
                        aria-hidden
                    >
                        <rect x="6" y="3" width="12" height="18" rx="6" />
                        <line x1="12" y1="7" x2="12" y2="11" />
                    </svg>
                </div>
            </div>
        </div>
    );
}
